import {cn} from '@sollapay/ui';
import {
  Button,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Typography
} from '@sollapay/ui/components';
import {useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {useTranslation} from 'react-i18next';

import {isAddBuyerTargetActionable, useAddBuyerTarget} from '@/features/buyers/hooks';
import {AddBuyerFlow, AddBuyerToPurchaseFlow, InitiatePaymentFlow} from '@/features/buyers/modals';
import {CreatePayoutFlow} from '@/features/payouts';
import {
  useCanWriteAnything,
  useCurrentTrustAccount,
  useTrustAccountPermissions
} from '@/features/trust-accounts/providers';
import {useCanCreatePaymentRequest, useShowsPayoutsTab} from '@/features/trust-accounts/view/hooks';

import type {IconName} from '@sollapay/ui/components';
import type {FC} from 'react';

interface QuickActionItem {
  id: string;
  label: string;
  icon: IconName;
  onClick: () => void;
  disabled?: boolean;
}

export const QuickActionsFab: FC = () => {
  const {t} = useTranslation('trustAccounts');
  const {id: trustAccountId} = useCurrentTrustAccount();
  const {canCreatePayout} = useTrustAccountPermissions();
  const canTransferPayment = useShowsPayoutsTab() && canCreatePayout;
  const addBuyerTarget = useAddBuyerTarget();
  const canCreatePaymentRequest = useCanCreatePaymentRequest();

  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isRequestPaymentOpen, setIsRequestPaymentOpen] = useState(false);
  const [isTransferPaymentOpen, setIsTransferPaymentOpen] = useState(false);
  const addBuyerOpenRef = useRef<(() => void) | null>(null);
  const addBuyerToPurchaseOpenRef = useRef<(() => void) | null>(null);

  const isExpanded = isOpen || isHovered;

  const items: QuickActionItem[] = useMemo(() => {
    const allItems: Array<QuickActionItem | null> = [
      canCreatePaymentRequest
        ? {
            id: 'request-payment',
            label: t('view.buyers.tab.paymentRequest'),
            icon: 'sollapay:payin',
            onClick: () => {
              setIsOpen(false);
              setIsRequestPaymentOpen(true);
            }
          }
        : null,
      canTransferPayment
        ? {
            id: 'transfer-payment',
            label: t('view.quickActions.transferPayment'),
            icon: 'sollapay:payout',
            onClick: () => {
              setIsOpen(false);
              setIsTransferPaymentOpen(true);
            }
          }
        : null,
      {
        id: 'add-buyer',
        label: t('view.buyers.addBuyerModal.title'),
        icon: 'sollapay:add-buyer',
        onClick: () => {
          setIsOpen(false);
          if (addBuyerTarget.kind === 'existing-purchase') {
            addBuyerToPurchaseOpenRef.current?.();
          } else if (addBuyerTarget.kind === 'new-purchase') {
            addBuyerOpenRef.current?.();
          }
        },
        disabled: !isAddBuyerTargetActionable(addBuyerTarget)
      }
    ];
    return allItems.filter((item): item is QuickActionItem => item !== null);
  }, [t, addBuyerTarget, canTransferPayment, canCreatePaymentRequest]);

  const canWriteAnything = useCanWriteAnything();
  if (!canWriteAnything) return null;

  return (
    <>
      {hasInteracted && (
        <>
          <AddBuyerFlow
            trustAccountId={trustAccountId}
            trigger={onOpen => {
              addBuyerOpenRef.current = onOpen;
              return null;
            }}
          />
          {addBuyerTarget.kind === 'existing-purchase' && (
            <AddBuyerToPurchaseFlow
              trustAccountId={trustAccountId}
              purchaseId={addBuyerTarget.purchaseId}
              trigger={onOpen => {
                addBuyerToPurchaseOpenRef.current = onOpen;
                return null;
              }}
            />
          )}
          {canCreatePaymentRequest && (
            <InitiatePaymentFlow
              trustAccountId={trustAccountId}
              isOpen={isRequestPaymentOpen}
              onClose={() => setIsRequestPaymentOpen(false)}
            />
          )}
          {canTransferPayment && (
            <CreatePayoutFlow
              trustAccountId={trustAccountId}
              isOpen={isTransferPaymentOpen}
              onClose={() => setIsTransferPaymentOpen(false)}
            />
          )}
        </>
      )}

      {createPortal(
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger
            render={
              <Button
                type="button"
                variant="primary"
                elevated
                aria-haspopup="menu"
                onClick={() => setHasInteracted(true)}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                className={cn(
                  'fixed bottom-20 end-6 z-40 h-14 min-w-14 rounded-full px-4 transition-[gap] duration-[180ms] ease',
                  isExpanded ? 'gap-2' : 'gap-0'
                )}
              >
                <Icon
                  name="plus"
                  className={cn(
                    'size-5 shrink-0 text-fg_on-brand transition-transform duration-[180ms] ease',
                    isOpen && 'rotate-45'
                  )}
                />
                <span
                  className={cn(
                    'flex items-center overflow-hidden transition-[max-width,opacity] duration-[180ms] ease',
                    isExpanded ? 'max-w-48 opacity-100' : 'max-w-0 opacity-0'
                  )}
                >
                  <span className="inline-block w-max">
                    <Typography size="md" className="text-fg_on-brand">
                      {t('view.quickActions.header')}
                    </Typography>
                  </span>
                </span>
              </Button>
            }
          />

          <PopoverContent
            side="top"
            align="end"
            sideOffset={12}
            positionerClassName="z-40"
            role="menu"
            aria-label={t('view.quickActions.header')}
            className={cn(
              'z-40 w-max min-w-41 flex-col rounded-2xl border border-border bg-background-surface p-2.25 shadow-lg ring-0',
              'data-open:animate-none data-closed:animate-none',
              'opacity-0 translate-y-2.5 scale-[0.96] pointer-events-none',
              'data-open:opacity-100 data-open:translate-y-0 data-open:scale-100 data-open:pointer-events-auto',
              hasInteracted && 'transition-[opacity,transform] duration-[160ms] ease-in-out'
            )}
          >
            <div className="px-3 pt-1 pb-2">
              <Typography size="xs" weight="semibold" className="text-fg-quaternary">
                {t('view.quickActions.header')}
              </Typography>
            </div>
            {items.map(item => (
              <QuickActionMenuItem key={item.id} item={item} />
            ))}
          </PopoverContent>
        </Popover>,
        document.body
      )}
    </>
  );
};

interface QuickActionMenuItemProps {
  item: QuickActionItem;
}

const QuickActionMenuItem: FC<QuickActionMenuItemProps> = ({item}) => (
  <button
    type="button"
    role="menuitem"
    disabled={item.disabled}
    onClick={item.onClick}
    className={cn(
      'flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-2.25',
      'hover:bg-background-surface_hover',
      item.id === 'transfer-payment' && 'bg-background-secondary',
      item.disabled && 'cursor-not-allowed opacity-50'
    )}
  >
    <span className="flex size-8.5 shrink-0 items-center justify-center rounded-full border border-border bg-background-secondary">
      <Icon name={item.icon} width={20} height={20} />
    </span>
    <Typography size="sm" weight="medium" className="text-fg-secondary">
      {item.label}
    </Typography>
  </button>
);
